import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRouteSnapshot, CanDeactivate, Router, RouterStateSnapshot } from '@angular/router';
import { forkJoin, isObservable, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

export interface CanComponentDeactivate {
  canDeactivate: (currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot) => boolean | Observable<boolean>;
}

@Injectable({providedIn: 'root'})
export class CanDeactivateGuard implements CanDeactivate<CanComponentDeactivate> {

  private _deactivationHandlers: Array<() => Observable<boolean> | boolean> = [
    () => of(true)
  ];

  static checkMultipleComponentsDeactivation(components: Array<CanComponentDeactivate>, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> {
    const results = components.map(c => {
      const canComponentDeactivate = c.canDeactivate(currentRoute, currentState, nextState);
      return canComponentDeactivate instanceof Observable
        ? canComponentDeactivate
        : of(canComponentDeactivate);
    });
    return forkJoin(results).pipe(
      map((res: Array<boolean>) => res.every(r => r))
    );
  }

  constructor(private _router: Router, private _location: Location) {
  }

  canDeactivate(component: CanComponentDeactivate, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState: RouterStateSnapshot): Observable<boolean> | boolean {
    // @ts-ignore
    if (this._router.getCurrentNavigation()?.extras?.state?.bypassDeactivateGuard) {
      return of(true);
    }
    return forkJoin(this._deactivationHandlers.map(h => h()).map(hRes => isObservable(hRes) ? hRes : of(hRes))).pipe(
      mergeMap(handlersResults => {
        const allowedByHandlers = !handlersResults.some(allowed => !allowed);
        if (allowedByHandlers) {
          const canComponentDeactivate: boolean | Observable<boolean> = component && component.canDeactivate ? component.canDeactivate(currentRoute, currentState, nextState) : true;
          if (isObservable(canComponentDeactivate)) {
            return canComponentDeactivate;
          } else {
            return of(canComponentDeactivate);
          }
        } else {
          return of(false);
        }
      }),
      map((canDeactivate: boolean) => {
        if (!canDeactivate) {
          // workaround for https://github.com/angular/angular/issues/13586
          const nav = this._router.getCurrentNavigation();
          if (nav && nav.trigger === 'popstate') {
            this._location.go(currentState.url);
          }
        }
        return canDeactivate;
      })
    );
  }

  registerDeactivationHandler(handler: () => Observable<boolean> | boolean) { // used by DialogManager to block back navigation when some dialog is opened
    this._deactivationHandlers.push(handler);
  }
}
